<template>
    <div>
        <h1>{{ $t('pages.login.title') }}</h1>
        <!-- form for logging into the application-->
        <b-form action="" method="POST" autocomplete="off" id="login" @submit="loginStarted=true">
            <input type="hidden" name="_token" :value="token" autocomplete="false">
            <!-- username field -->
            <b-form-group :label="$t('pages.login.username')" for="username" :state="userAllowed">
                <b-input-group>
                    <b-form-input type="text" name="username" v-model="username" :state="userAllowed" autofocus
                                  :placeholder="$t('pages.login.rzaccount')" dusk="username"></b-form-input>
                    <b-input-group-append-icon icon="person"/>

                    <!-- give the user feedback if the account name is invalid -->
                    <b-form-invalid-feedback>
                        <template v-if="username.length">
                            <!-- callout for invalid username -->
                            <callout v-if="!usernameValid" callout-class="callout-danger" no-icon class="mb-0">
                                {{$t('pages.login.username_invalid')}}
                            </callout>

                            <!-- callout for locked users -->
                            <callout v-if="userLockedUntil" callout-class="callout-danger" no-icon class="mb-0">
                                <p>{{ $t('pages.login.user_locked') }}</p>
                                <p class="text-bold">{{ $d(userLockedUntil, 'long') }}</p>
                            </callout>

                            <!-- callout for users that have no more grace logins left and an expired password -->
                            <callout v-if="userLockedOut" callout-class="callout-danger" class="mb-0">
                                <p>{{ $t("pages.login.user_locked_out_notice")}}</p>
                                <b-button :href="'/reset-access/'+this.username" variant="primary" style="color: white;">
                                    <b-icon-arrow-right/>
                                    {{ $t("pages.login.user_locked_out_button")}}
                                </b-button>
                            </callout>
                        </template>
                    </b-form-invalid-feedback>
                </b-input-group>
            </b-form-group>

            <!-- password field -->
            <b-form-group :label="$t('pages.login.password')" for="password" class="mt-auto" :state="passwordEntered">
                <b-input-group>
                    <b-form-input type="password" name="password" ref="password" v-model="password"
                                  :placeholder="$t('pages.login.password')" :state="passwordEntered" dusk="password"></b-form-input>
                    <b-input-group-append-icon icon="lock"/>
                </b-input-group>
            </b-form-group>

            <!-- fancy submit button that shows a loading indicator which will run indefinitely until the page has reloaded -->
            <b-overlay rounded opacity="0.6" spinner-small spinner-variant="primary" class="block mb-1" :show="loginStarted">
                <template #overlay>
                    <b-spinner small type="grow" variant="secondary"></b-spinner>
                    <span class="sr-only">{{ $t('common.please_wait') }}</span>
                </template>
                <b-button block type="submit" variant="primary" :disabled="!loginPossible" dusk="login-button">
                    <b-icon-arrow-right/>
                    {{ $t('pages.login.submit') }}
                </b-button>
            </b-overlay>

            <b-button size="sm" block href="/forgot">
                {{ $t('pages.login.lost') }}
            </b-button>
        </b-form>
    </div>
</template>

<script>
import LoadingInjector from "../../mixins/LoadingInjector";
import BInputGroupAppendIcon from "../layout/BInputGroupAppendIcon";

export default {
    components: {BInputGroupAppendIcon},
    mixins: [
        LoadingInjector
    ],
    props: {
        regexAccount: {
            type: String,
            required: true
        },
        checkLockedUrl: {
            type: String,
            required: true
        },
        checkLockedOutUrl: {
            type: String,
            required: true
        },
        token: {
            type: String,
            required: true
        }
    },
    data: function () {
        return {
            username: '',
            password: '',
            checkInProgress: 0,
            userLockedUntil: false,
            userLockedOut: false,
            loginStarted: false,
            debounce: 1000
        };
    },
    methods: {
        checkLockedState: function() {
            this.checkInProgress += 1;
            this.axios.get(`${this.checkLockedUrl}${this.username}`)
                .then(response => {
                    this.userLockedUntil = response.data.data['locked_until'] == null
                                           ? false
                                           : new Date(lockedState.locked_until);
                })
                .catch(error => {
                    console.log(`Failed retrieving locked status for account ${this.username}. Error: ${e}`)
                    this.userLockedUntil = null;
                })
                .finally(() => {
                    this.checkInProgress -= 1;
                })
        },
        checkLockedOutState: async function() {
            this.checkInProgress += 1;
            this.axios.get(`${this.checkLockedOutUrl}${this.username}`)
                .then(response => {
                    this.userLockedOut = response.data.data['mustAccessReset'];
                })
                .catch(error => {
                    console.log(`Failed retrieving locked-out status for account ${this.username}. Error: ${e}`);
                    this.userLockedOut = null;
                })
                .finally(() => {
                    this.checkInProgress -= 1;
                })
        }
    },
    computed: {
        usernameValid: function () {
            return this.username.length
                ? RegExp(`^${this.regexAccount}$`).test(this.username)
                : null; // null is neither valid nor invalid
        },
        passwordEntered: function () {
            return this.password.length
                ? (this.password.length > 3)
                : null; // null is neither valid nor invalid
        },
        userAllowed: function () {
            return this.usernameValid               // username is valid
                && this.userLockedUntil === false   // user is not locked because of rate limiting
                && this.userLockedOut === false;    // user is not locked out because she has no more grace logins left
        },
        loginPossible: function () {
            return this.userAllowed === true
                && this.passwordEntered === true
                && this.checkInProgress === 0;
        }
    },
    watch: {
        usernameValid: function(valid) {
            if(valid) {
                this.checkLockedState();
                this.checkLockedOutState();
            }
        }
    }
}
</script>
